'use client';

import { contentImageAlt } from '@communio/content-image-alt';

import { useRef, useState } from 'react';
import { updateVendorProduct } from './api';
import {
  MAX_PRODUCT_IMAGES,
  PRODUCT_IMAGE_ACCEPT,
  productImageDisplayUrl,
  uploadProductImage,
} from './productUploads';

export interface ProductImageGalleryEditorProps {
  vendorUserId: string;
  productId: string;
  imageObjectPaths: string[];
  imageUrls?: string[];
  readOnly?: boolean;
  onChange: (paths: string[], urls?: string[]) => void;
}

export function ProductImageGalleryEditor({
  vendorUserId,
  productId,
  imageObjectPaths,
  imageUrls,
  readOnly = false,
  onChange,
}: ProductImageGalleryEditorProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  async function persistPaths(paths: string[]) {
    const updated = await updateVendorProduct(vendorUserId, productId, { imageObjectPaths: paths });
    onChange(updated.imageObjectPaths ?? paths, updated.imageUrls);
  }

  async function handleFiles(files: FileList | null) {
    if (!files?.length || readOnly) return;
    setError('');
    const remaining = MAX_PRODUCT_IMAGES - imageObjectPaths.length;
    if (remaining <= 0) {
      setError(`Maximum ${MAX_PRODUCT_IMAGES} images per product.`);
      return;
    }
    setUploading(true);
    try {
      let paths = [...imageObjectPaths];
      let urls = imageUrls ? [...imageUrls] : undefined;
      for (const file of Array.from(files).slice(0, remaining)) {
        const objectPath = await uploadProductImage(file, vendorUserId, productId);
        paths = [...paths, objectPath];
        const display = productImageDisplayUrl(objectPath);
        urls = urls ? [...urls, display] : [display];
      }
      await persistPaths(paths);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not upload image');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  async function removeAt(index: number) {
    if (readOnly) return;
    const paths = imageObjectPaths.filter((_, i) => i !== index);
    setUploading(true);
    setError('');
    try {
      await persistPaths(paths);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not update gallery');
    } finally {
      setUploading(false);
    }
  }

  async function move(index: number, delta: number) {
    if (readOnly) return;
    const target = index + delta;
    if (target < 0 || target >= imageObjectPaths.length) return;
    const paths = [...imageObjectPaths];
    [paths[index], paths[target]] = [paths[target], paths[index]];
    setUploading(true);
    setError('');
    try {
      await persistPaths(paths);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not reorder gallery');
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="vendor-product-gallery">
      <h4>Product images</h4>
      <p className="text-muted vendor-console__hint">
        Optional gallery (up to {MAX_PRODUCT_IMAGES}). First image is the storefront thumbnail.
      </p>
      {error && <p className="form-error">{error}</p>}
      <ul className="vendor-product-gallery__grid">
        {imageObjectPaths.map((path, index) => (
          <li key={path} className="vendor-product-gallery__item">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={productImageDisplayUrl(path, imageUrls, index)}
              alt={contentImageAlt(null, `Product image ${index + 1}`)}
              className="vendor-product-gallery__thumb"
            />
            {!readOnly && (
              <div className="vendor-product-gallery__actions">
                <button
                  type="button"
                  className="btn btn-ghost"
                  disabled={uploading || index === 0}
                  onClick={() => void move(index, -1)}
                  aria-label="Move image left"
                >
                  ←
                </button>
                <button
                  type="button"
                  className="btn btn-ghost"
                  disabled={uploading || index === imageObjectPaths.length - 1}
                  onClick={() => void move(index, 1)}
                  aria-label="Move image right"
                >
                  →
                </button>
                <button
                  type="button"
                  className="btn btn-link"
                  disabled={uploading}
                  onClick={() => void removeAt(index)}
                >
                  Remove
                </button>
              </div>
            )}
          </li>
        ))}
      </ul>
      {!readOnly && imageObjectPaths.length < MAX_PRODUCT_IMAGES && (
        <>
          <input
            ref={inputRef}
            type="file"
            accept={PRODUCT_IMAGE_ACCEPT}
            className="vendor-product-gallery__file-input"
            disabled={uploading}
            onChange={(e) => void handleFiles(e.target.files)}
          />
          <button
            type="button"
            className="btn btn-secondary"
            disabled={uploading}
            onClick={() => inputRef.current?.click()}
          >
            {uploading ? 'Uploading…' : 'Add image'}
          </button>
        </>
      )}
    </div>
  );
}
